// src/pages/LoginPage.tsx
//
// 로그인 게이트 — 미인증 사용자에게 노출되는 유일한 화면.
// 좌측 다크 브랜드 패널 + 우측 로그인 카드 구성.
// (전역 theme.ts 의 매니페스트 스타일은 이 화면에서 sx 로 무력화한다)

import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { designTokens } from '../theme.ts';

// PrivateRoute.tsx 와 동일한 값을 유지해야 토큰 캐시가 재사용된다.
const AUDIENCE = 'https://sales.ourmars.net/api';
const SCOPE =
  'openid profile email offline_access read:estimates write:estimates profile:read profile:write';

const PANEL_WIDTH = 560;

const C = {
  canvas: '#F6F7F8',
  surface: '#FFFFFF',
  border: '#E4E7EA',
  ink: '#171A1E',
  sub: '#6B7178',
  accent: '#E2451C',
  accentHover: '#C93A14',
  panelBar: '#2F343A',
  panelCaption: '#8A9097',
  panelBody: '#9AA0A6',
  panelFoot: '#6B7178',
  shadow: '0 8px 24px rgba(16,24,32,0.06)',
};

const SANS = designTokens.fonts.sans;
const DISPLAY = designTokens.fonts.display;

export default function LoginPage() {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0();

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: C.canvas,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: C.accent }} />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = () => {
    loginWithRedirect({
      appState: { returnTo: '/dashboard' },
      authorizationParams: { audience: AUDIENCE, scope: SCOPE },
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: C.canvas,
        fontFamily: SANS,
      }}
    >
      {/* ─── 좌측 브랜드 패널 (md 미만에서는 숨김) ─── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          flex: `0 0 ${PANEL_WIDTH}px`,
          width: PANEL_WIDTH,
          bgcolor: C.ink,
          px: 7,
          py: 6,
        }}
      >
        {/* 워드마크 */}
        <Box>
          <Typography
            component="div"
            sx={{
              fontFamily: DISPLAY,
              fontWeight: 800,
              fontSize: 28,
              lineHeight: 1,
              color: '#FFFFFF',
              letterSpacing: '-0.01em',
            }}
          >
            MARS
            <Box component="span" sx={{ color: C.accent }}>
              .
            </Box>
          </Typography>
          <Box
            sx={{
              mt: 1.25,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.22em',
              color: C.panelCaption,
            }}
          >
            SALES MANAGEMENT
          </Box>
        </Box>

        {/* 중앙 카피 */}
        <Box sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4 }}>
            <Box sx={{ width: 120, height: 6, borderRadius: '3px', bgcolor: C.panelBar }} />
            <Box sx={{ width: 72, height: 6, borderRadius: '3px', bgcolor: C.accent }} />
            <Box sx={{ width: 96, height: 6, borderRadius: '3px', bgcolor: C.panelBar }} />
          </Box>

          <Typography
            component="h1"
            sx={{
              fontFamily: SANS,
              fontWeight: 700,
              fontSize: 40,
              lineHeight: 1.3,
              letterSpacing: '-0.02em',
              color: '#FFFFFF',
              mb: 2.5,
            }}
          >
            컨테이너 임대·판매를
            <br />
            한 화면에서.
          </Typography>

          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: 15,
              lineHeight: 1.7,
              color: C.panelBody,
              maxWidth: 400,
            }}
          >
            견적 작성부터 회차별 세금계산서 발행 일정까지, 마르스 영업관리 시스템이 흐름을 이어 줍니다.
          </Typography>
        </Box>

        {/* 푸터 */}
        <Box sx={{ fontSize: 12, color: C.panelFoot, letterSpacing: '0.02em' }}>
          © 2026 MARS · OURMARS.NET
        </Box>
      </Box>

      {/* ─── 우측 로그인 카드 ─── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2.5, sm: 4 },
          py: 6,
          bgcolor: C.canvas,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            bgcolor: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: '12px',
            boxShadow: C.shadow,
            p: 4,
          }}
        >
          {/* md 미만에서는 좌측 패널이 사라지므로 카드 안에 워드마크를 노출 */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
            <Typography
              component="div"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 800,
                fontSize: 24,
                lineHeight: 1,
                color: C.ink,
                letterSpacing: '-0.01em',
              }}
            >
              MARS
              <Box component="span" sx={{ color: C.accent }}>
                .
              </Box>
            </Typography>
          </Box>

          <Typography
            component="h2"
            sx={{
              fontFamily: SANS,
              fontWeight: 700,
              fontSize: 22,
              lineHeight: 1.3,
              color: C.ink,
              mb: 0.75,
            }}
          >
            로그인
          </Typography>
          <Typography sx={{ fontFamily: SANS, fontSize: 14, color: C.sub, mb: 3 }}>
            사내 계정으로 접속합니다.
          </Typography>

          <Button
            fullWidth
            disableElevation
            disableRipple={false}
            onClick={handleLogin}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: 52,
              px: 2.5,
              borderRadius: '8px',
              border: 'none',
              boxShadow: 'none',
              bgcolor: C.accent,
              color: '#FFFFFF',
              fontFamily: SANS,
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: 0,
              textTransform: 'none',
              transition: 'background-color 160ms ease',
              '&:hover': {
                bgcolor: C.accentHover,
                boxShadow: 'none',
                transform: 'none',
              },
              '&:active': { transform: 'none' },
            }}
          >
            로그인하고 시작하기
          </Button>

          <Divider sx={{ my: 3, borderColor: C.border }} />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 17, color: C.sub, mt: '1px', flexShrink: 0 }} />
            <Typography sx={{ fontFamily: SANS, fontSize: 13, lineHeight: 1.6, color: C.sub }}>
              처음이신가요? 가입 후 관리자 승인이 완료되면 이용할 수 있습니다.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
